import type { FinanceFormField } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import { FinanceFormFileInput } from '@/components/Financeiro/solicitacoes/FinanceFormFileInput'
import { Input, Textarea } from '@/components/ui/Input'
import { cn } from '@/utils/cn'

const selectClassName = cn(
  'w-full bg-[var(--glass-bg)] border border-[var(--glass-border)]',
  'text-[var(--text-primary)] rounded-md px-3 py-2 outline-none',
  'text-[13px] transition-colors duration-150',
  'focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary-muted)]',
)

type Props = {
  field: FinanceFormField
  value: string
  files: File[]
  error?: string
  onValueChange: (value: string) => void
  onFilesChange: (files: File[]) => void
}

export function isFinanceFieldFullWidth(field: FinanceFormField): boolean {
  return (
    field.type === 'textarea' ||
    field.type === 'file' ||
    field.name.includes('descricao') ||
    field.name.includes('motivo') ||
    field.name.includes('observacoes') ||
    field.name.includes('justificativa') ||
    field.name.includes('alteracoes')
  )
}

export function FinanceRequestFormField({
  field,
  value,
  files,
  error,
  onValueChange,
  onFilesChange,
}: Props) {
  const label = field.required ? `${field.label} *` : field.label

  if (field.type === 'file') {
    return (
      <FinanceFormFileInput
        id={`finance-file-${field.name}`}
        label={label}
        required={field.required}
        multiple={field.multiple ?? field.name.includes('anexo')}
        files={files}
        error={error}
        onChange={onFilesChange}
      />
    )
  }

  if (field.type === 'textarea') {
    return (
      <Textarea
        label={label}
        rows={4}
        value={value}
        placeholder={field.placeholder}
        error={error}
        onChange={(e) => onValueChange(e.target.value)}
      />
    )
  }

  if (field.type === 'select') {
    return (
      <div className='flex flex-col gap-1.5'>
        <label
          htmlFor={`finance-field-${field.name}`}
          className='font-medium text-[var(--font-color-label)] uppercase tracking-wider'
          style={{ fontSize: 'var(--font-size-label)' }}
        >
          {label}
        </label>
        <select
          id={`finance-field-${field.name}`}
          className={cn(selectClassName, error && 'border-[#e5484d]')}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
        >
          <option value=''>Selecione…</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p className='text-[#e5484d]' style={{ fontSize: 'var(--font-size-xs)' }}>
            {error}
          </p>
        )}
      </div>
    )
  }

  return (
    <Input
      label={label}
      type={field.type}
      value={value}
      placeholder={field.placeholder}
      error={error}
      onChange={(e) => onValueChange(e.target.value)}
    />
  )
}
